import {
  InspectionAssetType,
  InspectionQuestionType,
  type InspectionTemplate as GraphQLInspectionTemplateType,
  SourceType as GraphQLSourceType,
} from 'graphql-query-contracts';
import { SourceType as DomainSourceType } from 'constants-entities';
import {
  CURRENT_SCHEMA_VERSION,
  type InspectionTemplate,
  type TemplateConfig,
  type TemplateMetadata,
  type TemplateSection,
  type TemplateItem,
  type SectionConfig,
  type ItemConfig,
  type CompletionRules,
  type AssetType,
  type QuestionType,
  type SchemaVersion,
} from 'inspection-types';

export type GraphQLInspectionTemplate = GraphQLInspectionTemplateType;

function mapSchemaVersionToDomain(_schemaVersion: string): SchemaVersion {
  return CURRENT_SCHEMA_VERSION;
}

export function mapAssetTypeToDomain(assetType: InspectionAssetType): AssetType {
  const assetTypeMap: Record<InspectionAssetType, AssetType> = {
    [InspectionAssetType.Industrial]: 'industrial',
    [InspectionAssetType.Retail]: 'retail',
    [InspectionAssetType.Office]: 'office',
    [InspectionAssetType.Residential]: 'residential',
    [InspectionAssetType.MixedUse]: 'mixed_use',
  };

  return assetTypeMap[assetType] ?? 'office';
}

export function mapQuestionTypeToDomain(
  questionType: InspectionQuestionType
): QuestionType {
  const questionTypeMap: Record<InspectionQuestionType, QuestionType> = {
    [InspectionQuestionType.Rating_5Point]: 'rating_5_point',
    [InspectionQuestionType.Rating_3Point]: 'rating_3_point',
    [InspectionQuestionType.PassFail]: 'pass_fail',
    [InspectionQuestionType.OkDeficient]: 'ok_deficient',
    [InspectionQuestionType.ConditionAction]: 'condition_action',
    [InspectionQuestionType.YesNo]: 'yes_no',
    [InspectionQuestionType.Checklist]: 'checklist',
    [InspectionQuestionType.TextOnly]: 'text_only',
    [InspectionQuestionType.Numeric]: 'numeric',
    [InspectionQuestionType.Date]: 'date',
    [InspectionQuestionType.Select]: 'select',
    [InspectionQuestionType.MultiSelect]: 'multi_select',
    [InspectionQuestionType.Observation]: 'observation',
  };

  return questionTypeMap[questionType] ?? 'pass_fail';
}

function mapSourceTypeToDomain(
  graphqlSourceType: GraphQLSourceType
): DomainSourceType {
  const sourceTypeMap: Record<GraphQLSourceType, DomainSourceType> = {
    [GraphQLSourceType.SourceTypeActivate]: DomainSourceType.ACTIVATE,
    [GraphQLSourceType.SourceTypeYardi]: DomainSourceType.YARDI,
    [GraphQLSourceType.SourceTypeMri]: DomainSourceType.MRI,
    [GraphQLSourceType.SourceTypeRealPage]: DomainSourceType.REAL_PAGE,
    [GraphQLSourceType.SourceTypeEntrata]: DomainSourceType.ENTRATA,
    [GraphQLSourceType.SourceTypeVcm]: DomainSourceType.VCM,
    [GraphQLSourceType.SourceTypeSfdc]: DomainSourceType.SFDC,
    [GraphQLSourceType.SourceTypeCbre]: DomainSourceType.CBRE,
    [GraphQLSourceType.SourceTypeInvalid]: DomainSourceType.INVALID,
    [GraphQLSourceType.Unrecognized]: DomainSourceType.INVALID,
  };

  return sourceTypeMap[graphqlSourceType] ?? DomainSourceType.INVALID;
}

function mapItemConfigToDomain(
  config: GraphQLInspectionTemplate['sections'][number]['items'][number]['config']
): ItemConfig | undefined {
  if (!config) return undefined;

  return {
    requirePhotoOnDeficiency: config.requirePhotoOnDeficiency ?? undefined,
    requireNoteOnDeficiency: config.requireNoteOnDeficiency ?? undefined,
    minPhotos: config.minPhotos ?? undefined,
    isComplianceItem: config.isComplianceItem ?? false,
    tags: config.tags ?? undefined,
    category: config.category ?? undefined,
  };
}

function mapSectionConfigToDomain(
  config: GraphQLInspectionTemplate['sections'][number]['config']
): SectionConfig | undefined {
  if (!config) return undefined;

  return {
    requirePhotoOnDeficiency: config.requirePhotoOnDeficiency ?? undefined,
    skippable: config.skippable ?? false,
    collapsedByDefault: config.collapsedByDefault ?? false,
  };
}

function mapCompletionRulesToDomain(
  rules: GraphQLInspectionTemplate['config']['completionRules']
): CompletionRules {
  return {
    allItemsRequired: rules.allItemsRequired,
    allowPartialSubmission: rules.allowPartialSubmission,
  };
}

function mapTemplateConfigToDomain(
  config: GraphQLInspectionTemplate['config']
): TemplateConfig {
  return {
    requirePhotoOnDeficiency: config.requirePhotoOnDeficiency,
    maxPhotosPerItem: config.maxPhotosPerItem,
    requireNoteOnDeficiency: config.requireNoteOnDeficiency,
    defaultQuestionType: mapQuestionTypeToDomain(config.defaultQuestionType),
    allowObservationItems: config.allowObservationItems,
    completionRules: mapCompletionRulesToDomain(config.completionRules),
  };
}

function mapTemplateMetadataToDomain(
  metadata: GraphQLInspectionTemplate['metadata']
): TemplateMetadata {
  return {
    name: metadata.name,
    description: metadata.description ?? undefined,
    templateType: metadata.templateType ?? undefined,
    assetTypes: metadata.assetTypes?.map(mapAssetTypeToDomain) ?? undefined,
    tags: metadata.tags ?? undefined,
    buildingId: metadata.buildingId
      ? {
          entityId: metadata.buildingId.sourceEntityId,
          entityType: metadata.buildingId.sourceEntityType,
          sourceType: mapSourceTypeToDomain(metadata.buildingId.sourceType),
        }
      : undefined,
    createdAt: metadata.createdAt ?? undefined,
    createdBy: metadata.createdBy ?? undefined,
    updatedAt: metadata.updatedAt ?? undefined,
    updatedBy: metadata.updatedBy ?? undefined,
  };
}

function mapTemplateItemToDomain(
  item: GraphQLInspectionTemplate['sections'][number]['items'][number]
): TemplateItem {
  return {
    id: item.id,
    label: item.label,
    helpText: item.helpText ?? undefined,
    order: item.order,
    questionType: mapQuestionTypeToDomain(item.questionType),
    questionConfig: item.questionConfig ?? undefined,
    required: item.required,
    photosEnabled: item.photosEnabled,
    notesEnabled: item.notesEnabled,
    config: mapItemConfigToDomain(item.config),
  };
}

function mapTemplateSectionToDomain(
  section: GraphQLInspectionTemplate['sections'][number]
): TemplateSection {
  return {
    id: section.id,
    name: section.name,
    description: section.description ?? undefined,
    order: section.order,
    items: section.items.map(mapTemplateItemToDomain),
    config: mapSectionConfigToDomain(section.config),
  };
}

export function mapGraphQLTemplateToDomain(
  template: GraphQLInspectionTemplate
): InspectionTemplate {
  return {
    id: template.id,
    schemaVersion: mapSchemaVersionToDomain(template.schemaVersion),
    version: template.version,
    parentTemplateId: template.parentTemplateId ?? undefined,
    locked: template.locked,
    parentTemplateLockedVersion:
      template.parentTemplateLockedVersion ?? undefined,
    sections: template.sections.map(mapTemplateSectionToDomain),
    config: mapTemplateConfigToDomain(template.config),
    metadata: mapTemplateMetadataToDomain(template.metadata),
  };
}

export function mapGraphQLTemplatesToDomain(
  templates: GraphQLInspectionTemplate[]
): InspectionTemplate[] {
  return templates.map(mapGraphQLTemplateToDomain);
}
